<script setup lang="ts">
import type { PositionCellValue, PositionColumn, PositionRow } from '@/components/finest-custom-position-list/types'
import FinestCustomPositionListCard from '@/components/FinestCustomPositionListCard.vue'
import { idOf, matchesBlankRow, usePersistedRows } from '@/composables/usePersistedRows'
import { isEmptyValue } from '@/utils/field-state'
import { requestJson } from '@/utils/postJson'
import ModernizationController from '@controllers/ModernizationController'
import { ulid } from 'ulid'
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import ModernizationData = App.Data.EstateObject.ModernizationData

const props = defineProps<{
  estateObject: EstateObjectData
  modernizations: ModernizationData[]
  improvementTypes: string[]
}>()

const { t } = useI18n()

const columns = computed<PositionColumn[]>(() => [
  { key: 'year', label: t('columns.year'), type: 'year', width: 'w-28' },
  {
    key: 'type',
    label: t('columns.type'),
    type: 'select',
    options: props.improvementTypes.map((type) => ({
      value: type,
      label: t(`realEstateImprovement.${type}`),
    })),
  },
  { key: 'price', label: t('columns.price'), type: 'currency' },
  {
    key: 'status',
    label: t('columns.status'),
    type: 'select',
    options: [
      { value: 'planned', label: t('status.planned') },
      { value: 'in_progress', label: t('status.in_progress') },
      { value: 'finished', label: t('status.finished') },
    ],
  },
])

/**
 * The status is what the progress says: nothing done yet, under way, or complete. It is stored as the
 * completion percentage plus the finished flag, so both stay consistent with what the list shows.
 */
type ModernizationStatus = 'planned' | 'in_progress' | 'finished'

const partialCompletion = 50

const statusOf = (modernization: ModernizationData): ModernizationStatus | null => {
  if (modernization.is_finished === null || modernization.is_finished === undefined) {
    return null
  }

  if (modernization.is_finished) {
    return 'finished'
  }

  return (modernization.completion_percentage ?? 0) > 0 ? 'in_progress' : 'planned'
}

const completionFor = (status: PositionCellValue, current: number): number => {
  if (status === 'finished') {
    return 100
  }

  if (status === 'planned') {
    return 0
  }

  return current > 0 && current < 100 ? current : partialCompletion
}

const toNumber = (value: unknown): number | null =>
  value === null || value === undefined || value === '' ? null : Number(value)

const toRow = (modernization: ModernizationData): PositionRow => ({
  key: modernization.id ?? '',
  id: modernization.id ?? null,
  cells: {
    year: modernization.year ?? null,
    type: modernization.type ?? null,
    price: toNumber(modernization.price),
    status: statusOf(modernization),
    completion: modernization.completion_percentage ?? null,
  },
})

const blankModernization = (): PositionRow => ({
  key: ulid(),
  id: null,
  cells: { year: null, type: null, price: null, status: null, completion: null },
})

const progressOf = (row: PositionRow) =>
  isEmptyValue(row.cells.status)
    ? {}
    : {
        is_finished: row.cells.status === 'finished',
        completion_percentage: completionFor(row.cells.status, Number(row.cells.completion ?? 0)),
      }

const toPayload = (row: PositionRow) => ({
  year: row.cells.year,
  type: row.cells.type,
  price: row.cells.price,
  ...progressOf(row),
})

const { rows, reset, addDraft, removeRow, persistChanges, persistOnLeave } = usePersistedRows({
  toPayload,
  isDraft: matchesBlankRow(toPayload, blankModernization),
  create: async (payload) =>
    idOf(
      await requestJson<ModernizationData>(
        'post',
        ModernizationController.store(props.estateObject.id ?? '').url,
        payload,
      ),
    ),
  update: (id, payload) => requestJson('patch', ModernizationController.update(id).url, payload),
  destroy: (id) => requestJson('delete', ModernizationController.destroy(id).url, undefined),
  reloadProps: ['modernizations', 'fieldStates', 'completion'],
})

reset(props.modernizations.map(toRow))

watch(
  () => props.modernizations,
  (modernizations) => reset(modernizations.map(toRow)),
)
</script>

<template>
  <div @focusout="persistOnLeave">
    <FinestCustomPositionListCard
      v-model:rows="rows"
      :title="t('title')"
      :columns="columns"
      :add-label="t('add')"
      :empty-label="t('empty')"
      editable-rows
      flush
      @add-row="addDraft(blankModernization())"
      @remove-row="removeRow"
      @commit="persistChanges" />
  </div>
</template>

<i18n>
{
  "de": {
    "title": "Modernisierungen",
    "add": "Modernisierung hinzufügen",
    "empty": "Noch keine Modernisierungen erfasst. Füge die erste Maßnahme hinzu.",
    "columns": {
      "year": "Jahr",
      "type": "Art",
      "price": "Kosten",
      "status": "Status"
    },
    "status": {
      "planned": "Geplant",
      "in_progress": "Im Prozess",
      "finished": "Abgeschlossen"
    }
  },
  "en": {
    "title": "Modernisations",
    "add": "Add modernisation",
    "empty": "No modernisations recorded yet. Add the first measure.",
    "columns": {
      "year": "Year",
      "type": "Type",
      "price": "Costs",
      "status": "Status"
    },
    "status": {
      "planned": "Planned",
      "in_progress": "In progress",
      "finished": "Completed"
    }
  }
}
</i18n>
